import React from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import PhoneAndroidOutlinedIcon from '@mui/icons-material/PhoneAndroidOutlined';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
import { useTheme } from '@mui/material/styles';
import SubCard from 'ui-component/cards/SubCard';

export type FacebookEmbeddedSignupReadinessCopy = {
  dialogTitle: string;
  introInfo: string;
  phoneWarningTitle: string;
  phoneWarningBody: string;
  items: {
    facebookAdmin: string;
    phoneFree: string;
    smsVerification: string;
    payment: string;
  };
  acknowledge: string;
  startButton: string;
  cancel: string;
};

/** כשאין מפתחות ב-locale — מונע קריסה (copy undefined) */
export const DEFAULT_FACEBOOK_EMBEDDED_SIGNUP_READINESS_COPY: FacebookEmbeddedSignupReadinessCopy = {
  dialogTitle: 'לפני חיבור WhatsApp',
  introInfo:
    'החיבור נעשה דרך Meta Embedded Signup. כדאי להכין מראש את הפרטים הבאים כדי לעבור את התהליך בלי לחזור אחורה.',
  phoneWarningTitle: 'שימו לב — מספר טלפון',
  phoneWarningBody:
    'מספר WhatsApp Business יכול להיות משויך לישות עסקית אחת. ודאו שהמספר זמין לשימוש ושאתם יכולים לקבל קוד SMS.',
  items: {
    facebookAdmin: 'גישת ניהול לעמוד העסק או לחשבון Meta Business Manager הרלוונטי',
    phoneFree: 'מספר טלפון פנוי לשם WhatsApp Business',
    smsVerification: 'יכולת לקבל SMS לאימות',
    payment: 'אמצעי תשלום (אם Meta יבקשו לצורך חיבור/מסוף)',
  },
  acknowledge: 'קראתי והבנתי — אני מוכן/ה להמשיך לחיבור',
  startButton: 'המשך לחיבור',
  cancel: 'ביטול',
};

export type FacebookEmbeddedSignupReadinessDialogProps = {
  open: boolean;
  onClose: () => void;
  onConfirmConnect: () => void | Promise<void>;
  connectBusy?: boolean;
  copy?: FacebookEmbeddedSignupReadinessCopy;
};

const checklistIconSx = { minWidth: 36, color: 'success.main' };

export function FacebookEmbeddedSignupReadinessDialog({
  open,
  onClose,
  onConfirmConnect,
  connectBusy = false,
  copy = DEFAULT_FACEBOOK_EMBEDDED_SIGNUP_READINESS_COPY,
}: FacebookEmbeddedSignupReadinessDialogProps) {
  const theme = useTheme();
  const [acknowledged, setAcknowledged] = React.useState(false);

  React.useEffect(() => {
    if (!open) {
      setAcknowledged(false);
    }
  }, [open]);

  const handleConfirm = () => {
    void onConfirmConnect();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth dir={theme.direction}>
      <DialogContent sx={{ pt: 2.5, pb: 1 }}>
        <SubCard
          title={
            <Typography variant="h6" component="span" fontWeight={600}>
              {copy.dialogTitle}
            </Typography>
          }
          sx={{
            bgcolor: (t) => (t.palette.mode === 'dark' ? 'grey.900' : 'info.lighter'),
            border: '1px solid',
            borderColor: 'info.light',
          }}
          contentSX={{ pt: 1.5 }}
        >
          <Stack spacing={1.5}>
            <Alert severity="info" variant="outlined" sx={{ alignItems: 'flex-start' }}>
              <Typography variant="body2" fontWeight={600} component="span">
                {copy.introInfo}
              </Typography>
            </Alert>

            <Alert severity="warning" variant="filled" sx={{ '& .MuiAlert-message': { width: '100%' } }}>
              <Typography variant="subtitle2" fontWeight={700} component="div" gutterBottom>
                {copy.phoneWarningTitle}
              </Typography>
              <Typography variant="body2" fontWeight={600} component="div">
                {copy.phoneWarningBody}
              </Typography>
            </Alert>

            <List dense disablePadding>
              <ListItem alignItems="flex-start" disableGutters sx={{ py: 0.75 }}>
                <ListItemIcon sx={checklistIconSx}>
                  <AdminPanelSettingsOutlinedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography variant="body2" fontWeight={600}>
                      {copy.items.facebookAdmin}
                    </Typography>
                  }
                />
              </ListItem>
              <ListItem alignItems="flex-start" disableGutters sx={{ py: 0.75 }}>
                <ListItemIcon sx={checklistIconSx}>
                  <PhoneAndroidOutlinedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography variant="body2" fontWeight={600}>
                      {copy.items.phoneFree}
                    </Typography>
                  }
                />
              </ListItem>
              <ListItem alignItems="flex-start" disableGutters sx={{ py: 0.75 }}>
                <ListItemIcon sx={checklistIconSx}>
                  <SmsOutlinedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography variant="body2" fontWeight={600}>
                      {copy.items.smsVerification}
                    </Typography>
                  }
                />
              </ListItem>
              <ListItem alignItems="flex-start" disableGutters sx={{ py: 0.75 }}>
                <ListItemIcon sx={checklistIconSx}>
                  <CreditCardOutlinedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography variant="body2" fontWeight={600}>
                      {copy.items.payment}
                    </Typography>
                  }
                />
              </ListItem>
            </List>

            <Box
              sx={{
                borderRadius: 1,
                px: 1.5,
                py: 1,
                bgcolor: (t) => (t.palette.mode === 'dark' ? 'action.hover' : 'warning.lighter'),
                border: '1px solid',
                borderColor: 'warning.light',
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={acknowledged}
                    onChange={(_, c) => setAcknowledged(c)}
                    color="primary"
                  />
                }
                label={
                  <Typography variant="body2" fontWeight={700}>
                    {copy.acknowledge}
                  </Typography>
                }
              />
            </Box>
          </Stack>
        </SubCard>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, pt: 0, flexWrap: 'wrap', gap: 1 }}>
        <Button onClick={onClose} color="inherit" disabled={connectBusy}>
          {copy.cancel}
        </Button>
        <Box sx={{ flex: '1 1 auto' }} />
        {acknowledged ? (
          <Button variant="contained" color="primary" onClick={handleConfirm} disabled={connectBusy}>
            {connectBusy ? <CircularProgress size={18} color="inherit" sx={{ marginInlineEnd: 1 }} /> : null}
            {copy.startButton}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}
